import { useState } from 'react';
import { useSlidingPill, useTabEnter } from '@ai-tutor/ui';
import { Video, Music, Brain, FileText, Layers3 } from 'lucide-react';
import { TextRenderer } from './TextRenderer';
import { VideoRenderer } from './VideoRenderer';
import { AudioRenderer } from './AudioRenderer';
import { MindmapRenderer } from './MindmapRenderer';
import { LessonHighlightsCarousel } from '../content/highlights/LessonHighlightsCarousel';
import { ComingSoon } from '@/components/core/common/feedback/ComingSoon';
import { PanelBoundary } from '@/components/core/common/boundaries/PanelBoundary';
import { useDataMode } from '@/contexts/DataModeContext';
import {
  TextLessonSkeleton,
  MindmapSkeleton,
  VideoSkeleton,
} from '../skeletons/PanelSkeletons';
import { StepGate } from '../common/StepGate';
import { useGenerateContent } from '@/hooks/lesson/useOnDemandContent';
import { isFeatureEnabled } from '@/config/app';
import { useLessonTabParam } from '@/hooks/lesson/useLessonTabParam';
import { openingMediaType } from './openingMediaType';
import type { LessonStyle, MediaType } from '@/types/lesson';
import type { StepView } from '@/types/pipeline';

interface MediaViewerProps {
  /**
   * Which media tab to open on.
   *
   * The active tab is otherwise local state with no prop, so every sub-tab
   * except Text was unreachable without clicking — including the mindmap, whose
   * renderer is the one leaf that draws an SVG tree rather than DOM and refuses
   * to draw at all if the panel stops giving its canvas a height.
   *
   * The fallback when the URL names no media tab (`?view=`, see
   * `useLessonTabParam`) — the URL wins when it does. No production call site
   * passes this; component stories do.
   */
  defaultMediaType?: MediaType;
  lessonId?: string;
  lessonStyle: LessonStyle;
  onProgressUpdate?: (mediaType: string, progress: number) => void;
  /**
   * True once the lesson's overall_status is terminal. Used to decide
   * whether an off-flow tab (status undefined in pipeline_status) shows
   * the "not part of this flow" placeholder (lesson still generating)
   * or falls through to the renderer's own Generate CTA (lesson done).
   */
  lessonDone?: boolean;
  /**
   * Fully-resolved per-step view map from the page-level poll. Each StepGate
   * reads `steps[step]` (state + in_flow + on_demand + error).
   */
  steps?: Record<string, StepView>;
  /**
   * True once the page has both lesson metadata + pipeline-status snapshot
   * loaded. While false, every `<StepGate>` here renders the fetch-mode
   * skeleton instead of guessing a state.
   */
  lessonReady?: boolean;
  /** Switches the lesson to its Practice tab; forwarded to the Text tab's
   *  end-of-lesson card. */
  onPractice?: () => void;
}

/** Human-readable name per media sub-tab - used by the per-sub-tab
 *  `PanelBoundary` so a crash surfaces as "Text panel failed" /
 *  "Mindmap panel failed" / etc. Matches the pattern used by
 *  PracticeViewer and ReferenceViewer for their sub-tabs. */
const MEDIA_VIEWS: readonly MediaType[] = [
  'mindmap',
  'text',
  'video',
  'audio',
  'highlights',
];

/** The Overview tab is `mindmap` in code but `?view=overview` in the URL:
 *  a student reads the address bar too. `?view=mindmap` still opens it. */
const MEDIA_URL_NAMES: Partial<Record<MediaType, string>> = {
  mindmap: 'overview',
};

const MEDIA_LABEL: Record<MediaType, string> = {
  text: 'Text',
  video: 'Video',
  audio: 'Podcast',
  mindmap: 'Overview',
  highlights: 'Highlights',
};

export function MediaViewer({
  lessonId,
  lessonStyle,
  onProgressUpdate,
  steps,
  lessonDone,
  lessonReady,
  onPractice,
  defaultMediaType = 'text',
}: MediaViewerProps) {
  const { isApi } = useDataMode();
  // Single mutation, scoped per-step via `pendingType`. Used by the
  // off-flow Generate CTA that <StepGate> renders for video.
  const generateContent = useGenerateContent(lessonId || '');

  // Video generation + doubt mode are gated behind a feature flag while the
  // pipeline is being fixed. When off, the Video tab (and everything inside
  // it - the Generate CTA, player, "Ask Doubt", and doubt replay) is hidden.
  const videoEnabled = isFeatureEnabled('video');
  // `?view=` — see `useLessonTabParam`. Video only when the flag is on, so a
  // `view=video` link with it off lands on Text rather than a hidden tab.
  // A lesson always opens on its Overview (the mindmap view) - what it
  // teaches, before the text. Decided once, so the view never switches
  // under the reader.
  const [openingView] = useState<MediaType>(() =>
    openingMediaType(steps, defaultMediaType)
  );
  const [activeMediaType, setActiveMediaType] = useLessonTabParam<MediaType>(
    'view',
    videoEnabled ? MEDIA_VIEWS : MEDIA_VIEWS.filter(v => v !== 'video'),
    openingView,
    MEDIA_URL_NAMES
  );
  const contentRef = useTabEnter<HTMLDivElement>(activeMediaType);
  const { containerRef: tabBarRef, pill } = useSlidingPill<HTMLDivElement>(
    activeMediaType,
    'rounded-full bg-primary shadow-sm'
  );

  // The overview (the lesson's map) comes first: what the lesson teaches,
  // before the text, podcast and highlights that teach it.
  const mediaTypes = [
    {
      id: 'mindmap' as MediaType,
      label: 'Overview',
      icon: Brain,
      description: 'The lesson at a glance: its concept map',
    },
    {
      id: 'text' as MediaType,
      label: 'Text',
      icon: FileText,
      description: 'Main lesson content',
    },
    ...(videoEnabled
      ? [
          {
            id: 'video' as MediaType,
            label: 'Video',
            icon: Video,
            description: 'AI-generated video lessons',
          },
        ]
      : []),
    {
      id: 'audio' as MediaType,
      label: 'Podcast',
      icon: Music,
      description: 'Podcast-style audio content',
    },
    {
      id: 'highlights' as MediaType,
      label: 'Highlights',
      icon: Layers3,
      description: 'Story-style concept highlights',
    },
  ];

  const renderMediaContent = () => {
    switch (activeMediaType) {
      case 'text':
        return (
          <StepGate
            lessonId={lessonId}
            step='text'
            steps={steps}
            lessonDone={lessonDone}
            lessonReady={lessonReady}
            skeleton={<TextLessonSkeleton />}
            streaming
          >
            <TextRenderer
              lessonStyle={lessonStyle}
              onPractice={onPractice}
              stepWriting={steps?.text?.state === 'processing'}
            />
          </StepGate>
        );
      case 'video':
        return (
          <StepGate
            lessonId={lessonId}
            step='video'
            steps={steps}
            lessonDone={lessonDone}
            lessonReady={lessonReady}
            skeleton={<VideoSkeleton />}
            isGenerating={generateContent.pendingType === 'video'}
            generate={{
              icon: Video,
              label: 'video',
              description:
                'AI-narrated video walkthrough of this lesson. Usually takes 5-15 min.',
              onGenerate: () => generateContent.mutate('video'),
            }}
          >
            <VideoRenderer
              lessonId={lessonId}
              onProgressUpdate={onProgressUpdate}
            />
          </StepGate>
        );
      case 'audio':
        // Not a pipeline step: podcasts are made per section, on demand, so
        // the tab lists the sections and gates each one itself.
        return (
          <AudioRenderer
            lessonId={lessonId}
            onProgressUpdate={onProgressUpdate}
          />
        );
      case 'mindmap':
        return (
          <StepGate
            lessonId={lessonId}
            step='mindmap'
            steps={steps}
            lessonDone={lessonDone}
            lessonReady={lessonReady}
            skeleton={<MindmapSkeleton />}
          >
            <MindmapRenderer onProgressUpdate={onProgressUpdate} />
          </StepGate>
        );
      case 'highlights':
        // The highlights artifact is generated by the backend pipeline
        // (`highlights_task`) and surfaced via /lessons/{id}/highlights.
        // Mock/empty modes don't have a real artifact to show, so they
        // fall through to a placeholder. In API mode the carousel owns
        // its own GET + polling and handles skeleton / failed / empty
        // states internally - we just pass the pipeline status through.
        if (!isApi || !lessonId) {
          return (
            <ComingSoon
              icon={Layers3}
              title='Lesson Highlights'
              description='Story-style concept highlights for this lesson.'
            />
          );
        }
        return (
          <LessonHighlightsCarousel
            lessonId={lessonId}
            steps={steps}
            lessonDone={lessonDone}
            lessonReady={lessonReady}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className='flex h-full flex-col'>
      {/* Media type sub-navigation */}
      {/* Scrolls sideways on a phone - Text / Podcast / Mindmap / Highlights
          is wider than 390px, and the last tab used to clip to a bare icon. */}
      <div className='flex items-center overflow-x-auto border-b border-border px-3 py-2 [scrollbar-width:none] sm:px-4 sm:py-3 [&::-webkit-scrollbar]:hidden'>
        <div
          ref={tabBarRef}
          className='relative flex w-max flex-shrink-0 items-center gap-2'
        >
          {pill}
          {mediaTypes.map(type => {
            const Icon = type.icon;
            const isActive = activeMediaType === type.id;

            return (
              <button
                key={type.id}
                onClick={() => setActiveMediaType(type.id)}
                data-pill-active={isActive ? 'true' : undefined}
                className={`tap-target relative z-10 flex flex-shrink-0 items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? 'text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                <Icon className='h-4 w-4' />
                <span>{type.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Media content area. `h-full` gives the wrapper a definite
          height so descendants' `h-full` (used by the shared Panel*
          state cards) resolves correctly and the cards vertically
          center. Padding is `p-4` for tabs whose scroll sits inside
          a bordered/sectioned container, but `0` for tabs whose own
          scrollbar should sit flush against the panel border -
          Text + Video + Podcast each handle their own inner padding
          for readability. Mindmap (canvas) and Highlights (full-bleed
          carousel) keep the outer padding. */}
      <div className='min-h-0 flex-1 overflow-hidden'>
        <div
          ref={contentRef}
          className={`h-full ${
            activeMediaType === 'text' ||
            activeMediaType === 'video' ||
            activeMediaType === 'audio'
              ? ''
              : 'p-4'
          }`}
        >
          {/* Per-sub-tab boundary keyed on the active media type - same
              shared `PanelBoundary` used by Practice / Appendix so all
              sub-tabs (Text / Mindmap / Video / Podcast / Highlights /
              Quiz / Flashcards / Sources / Glossary / Doubts / Extras)
              produce identical heading + description + recovery
              buttons. The key remounts the boundary on tab switch so a
              recovered tab doesn't inherit the crash from another. */}
          <PanelBoundary
            key={activeMediaType}
            name={MEDIA_LABEL[activeMediaType]}
          >
            {renderMediaContent()}
          </PanelBoundary>
        </div>
      </div>
    </div>
  );
}
